





































































































































































































































import { Invoice } from '@accounting/interfaces/invoice';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Order } from '@accounting/interfaces/order';
import moment from 'moment';
import ModulesHelper from '@core/services/modules.helper';
import ToolsHelper from '@core/services/tools.helper';
import PdfPage from '../../pdf-page/pdf-page.vue';
import InvoiceSummaryLines from './invoice-summary-lines.vue';
import InvoicePdfHeader from '../../invoice-pdf-header.vue';
import { OrderItem } from '@accounting/interfaces/order-item';

interface PageInfo {
  totalLines: number;
  maxReached: boolean;
  lines: OrderItem[];
}

@Component({
  name: 'bpc-invoice-summary',
  components: {
    'bpc-pdf-page': PdfPage,
    'bpc-invoice-summary-lines': InvoiceSummaryLines,
    'bpc-invoice-pdf-header': InvoicePdfHeader,

    // 'bpc-invoice-first-page': InvoiceFirstPage,
    // 'bpc-invoice-common-page': InvoiceCommonPage,
  },
})
export default class InvoiceSummary extends Vue {
  // NOTE: Using this props we can adjust the content to the pdf page, you can play
  // with this in order to fix the data to our needs
  @Prop({ type: Number, default: 30 }) readonly firstPageTotalLines: number;
  @Prop({ type: Number, default: 32 }) readonly commonPageTotalLines: number;
  @Prop({ type: Number, default: 3 }) readonly minLinesForTotalRow: number;
  @Prop({ type: Number, default: 5 }) readonly minLinesForFooter: number;
  @Prop({ type: Number, default: 349 }) readonly headerHeight: number; // heigth in px
  @Prop({ type: Number, default: 200 }) readonly footerHeight: number; // heigth in px

  @Prop(Object) readonly invoice: Invoice;
  @Prop(Boolean) readonly debug: boolean;
  @Prop(String) readonly invoiceCode: string;
  @Prop({ type: Boolean, default: false }) readonly groupSubventionOnInvoices: boolean;

  formatDate(date) {
    if (date) {
      return moment(String(date)).format('DD/MM/YYYY');
    }
  }
  formatTime(time): string {
    if (time) {
      return time.substr(0, 5);
    }
    return '';
  }
  formatPrice(value: string | number) {
    return ToolsHelper.formatPrice(value);
  }

  get totalRowInFirstPage(): boolean {
    return this.commonPagesLines.length === 0;
  }

  get footerInFirstPage(): boolean {
    return this.commonPagesLines.length === 0;
  }

  get totalRowNeedsNewPage(): boolean {
    // If the invoice is 1 page only check with firstPageTotalLines
    if (this.commonPagesLines.length === 0) {
      return this.firstPageTotalLines - this.firstPageInfo.totalLines < this.minLinesForTotalRow;
    }

    return (
      this.commonPageTotalLines - this.lastCommonPageInfo.totalLines < this.minLinesForTotalRow
    );

    // If the invoice has more than a page check with the last page
  }

  get footerNeedsNewPage(): boolean {
    // If the invoice is 1 page only check with firstPageTotalLines
    if (this.commonPagesLines.length === 0) {
      return (
        this.firstPageTotalLines - this.firstPageInfo.totalLines - this.minLinesForTotalRow <
        this.minLinesForFooter
      );
    }

    return (
      this.commonPageTotalLines - this.lastCommonPageInfo.totalLines - this.minLinesForTotalRow <
      this.minLinesForFooter
    );

    // If the invoice has more than a page check with the last page
  }

  get firstPageInfo(): PageInfo {
    return this.linesByPages[0];
  }

  get firstPageLines(): OrderItem[] {
    return this.firstPageInfo?.lines || [];
  }

  get lastCommonPageInfo(): PageInfo {
    return this.commonPagesLines.slice(-1)[0];
  }

  get lastCommonPageIndex(): number {
    return this.commonPagesLines.length - 1;
  }

  get commonPagesLines(): PageInfo[] {
    return this.linesByPages.filter((page, index) => {
      return index !== 0;
    });
  }

  get orderItems(): OrderItem[] {
    return (this.invoice?.orders.filter(o => o.status !== 'cancelled') || []).reduce(
      (res, order) => {
        res = res.concat(order.order_items);
        return res;
      },
      [],
    );
  }

  get ordersByArticleAndPriceId(): Record<string | number, OrderItem> {
    return this.orderItems.reduce((aggregation, orderItem) => {
      const priceIdIndex = orderItem.article_price_id || 'none';
      const articleId = orderItem.article_id || 'none';
      const price = orderItem.price || 'none'; // Add the price in the aggregation index to take into account conditional pricing
      const description = orderItem.description || 'none'; // Add the description in the aggregation index to take into account conditions added in the description
      const index = `${articleId}_${priceIdIndex}_${price}_${description}`;
      if (!aggregation[index]) {
        aggregation[index] = {
          article_id: orderItem.article_id,
          article_price_id: orderItem.article_price_id,
          article_name: orderItem.article_name,
          quantity: 0,
          price: orderItem.price,
          // prices: [],
          account_id: orderItem.account_id,
          analytic_code: orderItem.analytic_code,
          description: orderItem.description,
          total_price: 0,
          discount: 0,
          tax_rate: orderItem.tax_rate,
          total_tax: orderItem.total_tax,
        };
      }

      const lastAggregate = aggregation[index];

      aggregation[index] = {
        ...lastAggregate,
        quantity: orderItem.quantity + lastAggregate.quantity,
        total_price: orderItem.total_price + lastAggregate.total_price,
        // prices: lastAggregate.prices.concat([orderItem.price]),
        // We were summing the percentages, so we had more than 100% of discount
        // Now we calculate the amount of the discount based on the price
        discount: this.calculateAggregateDiscount(orderItem, lastAggregate.discount),
      };
      return aggregation;
    }, {});
  }

  calculateAggregateDiscount(orderItem: OrderItem, currentDiscount: number | string) {
    if (orderItem.discount) {
      if (orderItem.discount_type === 'fixed') {
        const totalDiscount =
          parseFloat(String(currentDiscount)) +
          (orderItem.price - orderItem.discount) * orderItem.quantity; // TODO this way of applying a fixed discount is weird
        return parseFloat(String(totalDiscount))
          .toFixed(2)
          .replace('.', ',');
      }
      const totalDiscount =
        parseFloat(String(currentDiscount)) +
        orderItem.price * orderItem.quantity * (orderItem.discount / 100);

      return parseFloat(String(totalDiscount))
        .toFixed(2)
        .replace('.', ',');
    } else {
      return currentDiscount;
    }
  }

  get summaryAggregation(): OrderItem[] {
    return Object.values(this.ordersByArticleAndPriceId) || [];
  }

  get linesByPages(): PageInfo[] {
    const res = [this.getEmptyOrderPageInfo()];
    let currentIndex = 0;

    this.summaryAggregation.forEach((line: OrderItem) => {
      const maxLines = currentIndex === 0 ? this.firstPageTotalLines : this.commonPageTotalLines;
      const currentPage = res[currentIndex];
      const linesDiff = maxLines - currentPage.totalLines - 1;
      if (linesDiff >= 0 && !currentPage.maxReached) {
        res[currentIndex] = this.addLineToPageInfo(currentPage, line, linesDiff);
      } else {
        currentPage.maxReached = true;
        res[currentIndex] = currentPage;
        const newPage = this.getEmptyOrderPageInfo();
        const newLinesDiff = this.commonPageTotalLines - 1;
        res[currentIndex + 1] = this.addLineToPageInfo(newPage, line, newLinesDiff);
        currentIndex++;
      }
    });

    return res;
  }

  getEmptyOrderPageInfo(): PageInfo {
    return { totalLines: 0, maxReached: false, lines: [] };
  }

  orderLinesAndPageLinesDiff(totalLines: number, order: Order, maxLines: number): number {
    const nLines = 1;
    const newLines = totalLines + nLines;
    return maxLines - newLines;
  }

  addLineToPageInfo(pageInfo: PageInfo, line: OrderItem, linesDiff: number): PageInfo {
    const nLines = 1;
    const newLines = pageInfo.totalLines + nLines;
    pageInfo.totalLines = newLines;
    pageInfo.lines.push(line);
    pageInfo.maxReached = linesDiff <= 0;

    return pageInfo;
  }

  addLinesToPageInfoArray(pageInfo: PageInfo[], line: OrderItem, position: number): PageInfo[] {
    const res = pageInfo;
    if (!res[position]) {
      res[position] = { totalLines: 0, maxReached: false, lines: [] };
    }
    res[position].lines.push(line);

    return res;
  }

  get invoiceCodeString(): string {
    return this.invoice.code ? '# ' + this.invoiceCode : this.invoiceCode;
  }

  getOrderTotalLines(order: Order) {
    const nItems = order?.order_items?.length || 0;
    return nItems + 1; // Because we always add a line with the title/desc
  }
  isTaxManagementModuleActivated(): boolean {
    return this.$modulesService.hasModuleActivated(ModulesHelper.TAX_MANAGEMENT_MODULE_NAME);
  }

  get invoiceText(): string {
    return 'global.invoice_type.' + this.invoice.invoice_type;
  }
  get invoiceDateText(): string {
    if (this.invoice.invoice_type == 'ticket') {
      return 'global.ticket_date.label';
    } else if (this.invoice.invoice_type == 'invoice') {
      return 'global.invoice_date.label';
    } else {
      return 'global.date.label';
    }
  }

  get invoiceHasSubvention(): boolean {
    return !!this.invoice?.orders.find(item => item.type === 'subvention');
  }

  get totalAmountOfSubventions(): number {
    let total = 0;
    this.invoice?.orders.forEach(item => {
      if (item.type === 'subvention') {
        total += item.total_price;
      }
    });
    return total;
  }

  get totalAmountWithoutSubvention(): number {
    let total = 0;
    this.invoice?.orders.forEach(item => {
      if (item.type !== 'subvention') {
        total += item.total_price;
      }
    });
    return total;
  }
}
